import { useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  Menu,
  X,
  Check,
  Search,
  Rocket,
  Building2,
  Zap,
  Landmark,
  LayoutDashboard,
  BarChart3,
  Award,
  Users,
  ShieldCheck,
  MapPin,
  Mail,
  ChevronRight,
  Sparkles,
  Phone,
  KeyRound,
  Calculator,
  CircuitBoard,
  ArrowLeftRight,
} from "lucide-react";

/**
 * LIVJN — one file, two audiences, one connection.
 *
 * The buyer page and the partner page used to be separate artifacts with
 * a footer line pretending to link between them. Here they're one
 * component with real state: a switcher in the nav actually moves you
 * from one experience to the other, no reload, same brand system.
 *
 * Photography (Pexels, license-free, no attribution required) is layered
 * into both pages — real listing/room photos on the buyer side, and
 * photography woven into two of the eight revenue-stream panels plus the
 * "Why It Works" and closing CTA sections on the partner side — so
 * neither page is text-and-diagrams only anymore.
 */

const PHOTOS = {
  houseExterior:
    "https://images.pexels.com/photos/7031607/pexels-photo-7031607.jpeg?auto=compress&cs=tinysrgb&w=800",
  houseExterior2:
    "https://images.pexels.com/photos/30484316/pexels-photo-30484316.jpeg?auto=compress&cs=tinysrgb&w=800",
  livingRoom:
    "https://images.pexels.com/photos/6238679/pexels-photo-6238679.jpeg?auto=compress&cs=tinysrgb&w=700",
  kitchen:
    "https://images.pexels.com/photos/7031213/pexels-photo-7031213.jpeg?auto=compress&cs=tinysrgb&w=700",
  bathroom:
    "https://images.pexels.com/photos/6198661/pexels-photo-6198661.jpeg?auto=compress&cs=tinysrgb&w=700",
  bedroom:
    "https://images.pexels.com/photos/8135502/pexels-photo-8135502.jpeg?auto=compress&cs=tinysrgb&w=700",
  agentMeeting:
    "https://images.pexels.com/photos/7415023/pexels-photo-7415023.jpeg?auto=compress&cs=tinysrgb&w=700",
  agent1:
    "https://images.pexels.com/photos/30004322/pexels-photo-30004322.jpeg?auto=compress&cs=tinysrgb&w=300",
  agent2:
    "https://images.pexels.com/photos/8815878/pexels-photo-8815878.jpeg?auto=compress&cs=tinysrgb&w=300",
  agent3:
    "https://images.pexels.com/photos/18809829/pexels-photo-18809829.jpeg?auto=compress&cs=tinysrgb&w=300",
  aerial:
    "https://images.pexels.com/photos/3997060/pexels-photo-3997060.jpeg?auto=compress&cs=tinysrgb&w=1400",
  newBuild:
    "https://images.pexels.com/photos/8482520/pexels-photo-8482520.jpeg?auto=compress&cs=tinysrgb&w=900",
  featuredHome:
    "https://images.pexels.com/photos/19689230/pexels-photo-19689230.jpeg?auto=compress&cs=tinysrgb&w=900",
};

const FONT_IMPORT_STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap');
  .font-display { font-family: 'Space Grotesk', sans-serif; }
  .font-body { font-family: 'Inter', sans-serif; }
  .font-mono-label { font-family: 'JetBrains Mono', monospace; letter-spacing: 0.04em; }

  @keyframes fadeInPanel { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
  .fade-panel { animation: fadeInPanel 0.4s ease-out; }

  @keyframes fadeInUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
  .enter-up { animation: fadeInUp 0.7s cubic-bezier(0.16, 1, 0.3, 1) both; }

  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  .enter-fade { animation: fadeIn 0.5s ease-out both; }

  @keyframes modalPop { from { opacity: 0; transform: translateY(14px) scale(0.96); } to { opacity: 1; transform: translateY(0) scale(1); } }
  .modal-pop { animation: modalPop 0.3s cubic-bezier(0.16, 1, 0.3, 1) both; }

  @keyframes softPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.55; } }
  .pulse-dot { animation: softPulse 2.2s ease-in-out infinite; }

  @keyframes viewIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
  .view-in { animation: viewIn 0.45s cubic-bezier(0.16, 1, 0.3, 1) both; }

  * { scroll-behavior: smooth; }
  button, a { transition: transform 0.2s ease, background-color 0.25s ease, color 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease; }
  button:active, a:active { transform: scale(0.97); }
  input[type="range"] { accent-color: #5B2E91; }
`;

function LivjnMark({ size = 36 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 44 44"
      fill="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient
          id="livjnBadgeS"
          x1="4"
          y1="2"
          x2="40"
          y2="42"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#7A46B8" />
          <stop offset="1" stopColor="#3D1F6B" />
        </linearGradient>
      </defs>
      <rect
        x="1.5"
        y="1.5"
        width="41"
        height="41"
        rx="11"
        fill="url(#livjnBadgeS)"
      />
      <rect
        x="1.5"
        y="1.5"
        width="41"
        height="41"
        rx="11"
        stroke="rgba(244,241,251,0.18)"
      />
      <line
        x1="17"
        y1="10"
        x2="12"
        y2="5.5"
        stroke="#C6963C"
        strokeWidth="1.6"
        strokeLinecap="round"
        opacity="0.85"
      />
      <line
        x1="17"
        y1="10"
        x2="22.5"
        y2="5.5"
        stroke="#C6963C"
        strokeWidth="1.6"
        strokeLinecap="round"
        opacity="0.85"
      />
      <line
        x1="17"
        y1="10"
        x2="17"
        y2="3.2"
        stroke="#C6963C"
        strokeWidth="1.6"
        strokeLinecap="round"
        opacity="0.6"
      />
      <rect x="14" y="10" width="6" height="24" rx="2" fill="#F4F1FB" />
      <rect x="14" y="28" width="16" height="6" rx="2" fill="#F4F1FB" />
      <circle cx="17" cy="10" r="3.4" fill="#C6963C" />
    </svg>
  );
}

function Reveal({ children, className = "", delay = 0, as: Tag = "div" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={`transition-all ease-out ${visible ? "opacity-100 translate-y-0 duration-700" : "opacity-0 translate-y-6 duration-0"} ${className}`}
      style={visible ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </Tag>
  );
}

/** ViewSwitch — the actual connection between the two pages. */
function ViewSwitch({ view, setView, dark }) {
  return (
    <div
      className={`hidden sm:flex items-center gap-0.5 rounded-full p-0.5 border ${
        dark
          ? "border-white/20 bg-white/5"
          : "border-[#170F26]/15 bg-[#170F26]/5"
      }`}
    >
      {[
        { key: "user", label: "Buyers" },
        { key: "partner", label: "Partners" },
      ].map((opt) => (
        <button
          key={opt.key}
          onClick={() => setView(opt.key)}
          className={`font-mono-label text-[10px] uppercase px-3 py-1.5 rounded-full transition-all ${
            view === opt.key
              ? "bg-[#C6963C] text-[#170F26]"
              : dark
                ? "text-[#E4D9F5]/70 hover:text-white"
                : "text-[#6B6478] hover:text-[#170F26]"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export default function IntroLivjnSite() {
  const [view, setView] = useState("user");
  return view === "user" ? (
    <UserPage
      switchToPartner={() => {
        setView("partner");
        window.scrollTo(0, 0);
      }}
      setView={setView}
      view={view}
    />
  ) : (
    <PartnerPage
      switchToUser={() => {
        setView("user");
        window.scrollTo(0, 0);
      }}
      setView={setView}
      view={view}
    />
  );
}

/* ============================== BUYER PAGE ============================== */

function UserPage({ switchToPartner, setView, view }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");
  const [showModal, setShowModal] = useState(false);
  const [income, setIncome] = useState(95000);
  const [deposit, setDeposit] = useState(80000);

  const navItems = [
    { label: "Search Homes", href: "#hero" },
    { label: "Why It's Free", href: "#free" },
    { label: "What You Get", href: "#features" },
    { label: "Afford Calculator", href: "#afford" },
    { label: "Why Livjn", href: "#why" },
  ];

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const ids = navItems.map((item) => item.href.replace("#", ""));
    const sections = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 },
    );
    sections.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const el = document.querySelector(id);
    if (!el) return;
    setTimeout(
      () => el.scrollIntoView({ behavior: "smooth", block: "start" }),
      150,
    );
  };

  const openModal = () => setShowModal(true);

  const FEATURES = [
    {
      icon: Search,
      title: "Search every real listing",
      desc: "Full property search, always free to use. No paywalled results, no 'sign up to see more'.",
      funds: "Funded by agent subscriptions, not by you.",
    },
    {
      icon: Rocket,
      title: "See what's worth seeing",
      desc: "Some homes are promoted by agents chasing extra reach — we label those clearly, so you always know what's organic.",
      funds: "Funded by promotion fees agents choose to pay.",
    },
    {
      icon: Building2,
      title: "Browse new builds early",
      desc: "New developments get their own project pages — floor plans, timelines, and pricing before they're finished.",
      funds: "Funded by developer advertising campaigns.",
    },
    {
      icon: Zap,
      title: "Talk to the actual agent",
      desc: "One click connects you straight to the person listing the home — no call centre, no reassignment queue.",
      funds: "Agents pay for the enquiry, not you.",
    },
    {
      icon: Landmark,
      title: "Know what you can borrow",
      desc: "A free pre-qualification check connects you to a bank or broker before you fall for a home you can't finance.",
      funds: "Lenders pay a referral fee only if a loan settles.",
    },
    {
      icon: BarChart3,
      title: "Read the suburb, not just the listing",
      desc: "Price trends, typical days on market, and demand by suburb — the same data we sell to banks, given to you free.",
      funds: "Funded by data subscriptions sold to the industry.",
    },
  ];

  const FUNDING = [
    { icon: Users, label: "Agent subscriptions" },
    { icon: Rocket, label: "Promoted listings" },
    { icon: Building2, label: "Developer campaigns" },
    { icon: Landmark, label: "Mortgage referrals" },
    { icon: BarChart3, label: "Market data subscriptions" },
  ];

  const PILLARS = [
    {
      icon: ShieldCheck,
      title: "Free for buyers and renters, always",
      desc: "Search, contact agents, and read market data without ever paying Livjn a cent. The agents and developers on the other side fund the platform.",
    },
    {
      icon: KeyRound,
      title: "One agent, start to finish",
      desc: "The person who answers your first enquiry stays on your file through viewing, offer, and settlement — no handoffs.",
      photo: PHOTOS.agent1,
    },
    {
      icon: Sparkles,
      title: "Built in public, from day one",
      desc: "Livjn is pre-launch. What you see here is the plan being built, not a finished product with history to hide.",
    },
  ];

  const ROOM_STRIP = [
    { photo: PHOTOS.livingRoom, label: "Living & Dining" },
    { photo: PHOTOS.kitchen, label: "Kitchen" },
    { photo: PHOTOS.bedroom, label: "Bedroom" },
    { photo: PHOTOS.bathroom, label: "Bathroom" },
  ];

  const borrowingEstimate = Math.round((income * 6 + deposit) / 1000) * 1000;

  return (
    <div className=" min-h-screen bg-[#F4F1FB] font-body text-[#170F26] overflow-x-hidden scroll-smooth">
      <style>{FONT_IMPORT_STYLE}</style>

      {/* NAVBAR */}
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          navScrolled
            ? "bg-[#4A2478] shadow-[0_8px_30px_rgba(23,15,38,0.45)] border-b border-white/10"
            : "bg-[#5B2E91]/95 backdrop-blur-md border-b border-white/5"
        }`}
      >
        <div
          className={`max-w-7xl mx-auto flex justify-between items-center px-6 transition-all duration-300 ${navScrolled ? "py-2.5" : "py-3.5"}`}
        >
          <button
            onClick={() => scrollToSection("#hero")}
            className="flex items-center gap-2.5"
          >
            <img src="/images/logolivjn_w.png" className="h-8" />
          </button>

          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => {
              const id = item.href.replace("#", "");
              const isActive = activeSection === id;
              return (
                <button
                  key={item.label}
                  onClick={() => scrollToSection(item.href)}
                  className={`relative px-3 py-2 text-[13px] font-medium uppercase tracking-wide whitespace-nowrap transition-colors ${
                    isActive
                      ? "text-[#F2CD82]"
                      : "text-[#E4D9F5]/80 hover:text-white"
                  }`}
                >
                  {item.label}
                  <span
                    className={`absolute left-3 right-3 -bottom-[1px] h-[2px] bg-[#F2CD82] rounded-full origin-center transition-transform duration-300 ease-out ${
                      isActive ? "scale-x-100" : "scale-x-0"
                    }`}
                  />
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-3">
            <ViewSwitch view={view} setView={setView} dark />
            <button
              onClick={openModal}
              className="hidden md:flex px-6 py-2.5 rounded-2xl bg-[#170F26] hover:bg-[#241A38] text-[#F4F1FB] text-sm font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all whitespace-nowrap items-center gap-1.5"
            >
              Get Early Access
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <button
              className="lg:hidden text-[#F4F1FB] p-2 -mr-2 rounded-2xl hover:bg-white/10 transition-colors"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <div className="flex items-center gap-x-3">
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      switchToPartner();
                    }}
                    className="font-mono-label text-[11px] uppercase text-[#F2CD82] flex items-center gap-1.5"
                  >
                    <ArrowLeftRight className="w-3.5 h-3.5" /> For Partner
                  </button>
                  <Menu className="w-6 h-6" />
                </div>
              )}
            </button>
          </div>
        </div>

        {menuOpen && (
          <>
            <div
              className="enter-fade fixed inset-0 bg-[#170F26]/90 backdrop-blur-sm z-40 h-screen lg:hidden mt-16"
              onClick={() => setMenuOpen(false)}
            />
            <div className="enter-up fixed top-16 left-0 right-0 z-50 lg:hidden bg-[#5B2E91] border-t border-white/15 flex flex-col items-center py-6 space-y-4">
              {navItems.map((item, i) => (
                <button
                  key={item.label}
                  onClick={() => {
                    setMenuOpen(false);
                    scrollToSection(item.href);
                  }}
                  className="enter-up text-lg text-[#F4F1FB] tracking-wide"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  {item.label}
                </button>
              ))}
              <span className="w-10 h-px bg-white/20 my-1" />

              <button
                onClick={() => {
                  setMenuOpen(false);
                  openModal();
                }}
                className="px-6 py-2.5 rounded-2xl bg-[#170F26] text-[#F4F1FB] text-sm font-semibold flex items-center gap-1.5"
              >
                Get Early Access <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </>
        )}
      </nav>

      {/* HERO */}
      <section
        id="hero"
        className="relative min-h-screen flex items-center px-6 md:px-16 pt-32 pb-20 bg-[#170F26] overflow-hidden"
      >
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#F4F1FB 1px, transparent 1px), linear-gradient(90deg, #F4F1FB 1px, transparent 1px)",
            backgroundSize: "34px 34px",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto w-full grid md:grid-cols-[1fr_1fr] gap-16 items-center">
          <div>
            <span className="enter-up inline-flex items-center gap-1.5 font-mono-label text-[10px] uppercase text-[#C6963C] border border-[#C6963C]/40 rounded-full px-3 py-1 mb-5">
              <Sparkles className="w-3 h-3 pulse-dot" /> Pre-Launch · Free For
              Every Buyer
            </span>

            <h1
              className="enter-up font-display font-semibold text-5xl md:text-6xl leading-[1.08] text-[#F4F1FB]"
              style={{ animationDelay: "80ms" }}
            >
              Find the home,
              <br />
              <span className="text-[#B18ADB]">not the runaround.</span>
            </h1>

            <p
              className="enter-up mt-6 max-w-lg text-[#C9C4D9] text-lg leading-relaxed"
              style={{ animationDelay: "160ms" }}
            >
              Livjn is a new property platform for Canterbury — still being
              built, with no live listings yet. When it opens, searching,
              contacting agents, and reading market data will always be free for
              you. Here's what that will look like.
            </p>

            <div
              className="enter-up mt-9 flex flex-wrap items-center gap-4"
              style={{ animationDelay: "240ms" }}
            >
              <button
                onClick={openModal}
                className="px-7 py-3 rounded-2xl bg-[#5B2E91] hover:bg-[#7A46B8] text-[#F4F1FB] font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all flex items-center gap-2"
              >
                Get Early Access <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => scrollToSection("#features")}
                className="px-7 py-3 rounded-2xl border border-[#F4F1FB]/25 text-[#F4F1FB] font-semibold hover:bg-[#F4F1FB]/10 hover:-translate-y-0.5 transition-all"
              >
                See What's Coming
              </button>
            </div>
          </div>

          <div
            className="relative enter-up"
            style={{ animationDelay: "200ms" }}
          >
            <span className="absolute -top-4 left-4 z-20 font-mono-label text-[10px] uppercase text-[#170F26] bg-[#C6963C] rounded-full px-3 py-1 shadow-lg">
              Example Listing · Not Live Yet
            </span>
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden transition-shadow duration-500 hover:shadow-[0_25px_60px_-15px_rgba(91,46,145,0.55)]">
              <div className="h-48 relative">
                <img
                  src={PHOTOS.houseExterior}
                  alt="Example listing exterior, for preview purposes only"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#170F26]/50 to-transparent" />
                <span className="absolute top-3 left-3 font-mono-label text-[10px] uppercase px-2.5 py-1 rounded-full bg-[#170F26]/70 text-white">
                  Preview
                </span>
              </div>
              <div className="p-6">
                <h3 className="font-display italic text-xl text-[#170F26] leading-snug">
                  9 Kōwhai Lane
                </h3>
                <p className="text-[#6B6478] text-sm mb-3">
                  Netherby, Ashburton
                </p>
                <p className="font-mono-label text-lg text-[#5B2E91] mb-4">
                  Price on request
                </p>
                <div className="flex items-center gap-4 text-[#6B6478] text-sm pt-3 border-t border-[#170F26]/10">
                  <span>4 bed</span>
                  <span>2 bath</span>
                  <span>2 car</span>
                </div>
                <button className="mt-5 w-full py-2.5 rounded-2xl bg-[#5B2E91]/10 text-[#5B2E91] font-semibold text-sm hover:bg-[#5B2E91]/15 transition-colors">
                  Contact Agent — Coming Soon
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROOM PREVIEW STRIP */}
      <section className="px-6 md:px-16 py-16 bg-white">
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center max-w-xl mx-auto mb-8">
            <p className="font-mono-label text-xs uppercase text-[#C6963C] mb-2">
              Example Listing
            </p>
            <h2 className="font-display font-semibold text-2xl md:text-3xl text-[#170F26]">
              A closer look at 9 Kōwhai Lane
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {ROOM_STRIP.map((room, i) => (
              <Reveal
                key={room.label}
                delay={i * 90}
                className="group relative rounded-2xl overflow-hidden aspect-[4/3] shadow-sm"
              >
                <img
                  src={room.photo}
                  alt={room.label}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#170F26]/70 via-transparent to-transparent" />
                <span className="absolute bottom-2 left-3 font-mono-label text-[10px] uppercase text-white">
                  {room.label}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY IT'S FREE */}
      <section id="free" className="px-6 md:px-16 py-24 bg-[#F4F1FB]">
        <div className="max-w-6xl mx-auto grid md:grid-cols-[1fr_1fr] gap-14 items-center">
          <Reveal>
            <p className="font-mono-label text-xs uppercase text-[#C6963C] mb-3">
              Why It's Free
            </p>
            <h2 className="font-display font-semibold text-4xl md:text-5xl text-[#170F26] mb-6 leading-tight">
              You never pay Livjn. Here's who does.
            </h2>
            <p className="text-[#4A4557] text-lg leading-relaxed">
              Livjn earns from the agents and developers on the other side of
              the platform, not from the people searching. Full transparency:
              this is what funds the free experience you get.
            </p>
          </Reveal>

          <Reveal
            delay={120}
            className="bg-white rounded-2xl border border-[#170F26]/8 shadow-sm p-6"
          >
            <p className="font-mono-label text-xs uppercase text-[#6B6478] mb-4">
              What funds Livjn
            </p>
            <div className="space-y-3">
              {FUNDING.map((f) => {
                const Icon = f.icon;
                return (
                  <div
                    key={f.label}
                    className="flex items-center gap-3 py-2.5 border-b border-[#170F26]/6 last:border-b-0 transition-transform duration-300 hover:translate-x-1"
                  >
                    <span className="w-9 h-9 rounded-2xl bg-[#5B2E91]/10 flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-[#5B2E91]" />
                    </span>
                    <span className="text-[#170F26] text-sm font-medium">
                      {f.label}
                    </span>
                    <ChevronRight className="w-4 h-4 text-[#A99BC4] ml-auto" />
                  </div>
                );
              })}
            </div>
            <p className="font-mono-label text-[11px] text-[#C6963C] mt-4">
              → Funds everything below, free for you
            </p>
          </Reveal>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section id="features" className="px-6 md:px-16 py-24 bg-white">
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <p className="font-mono-label text-xs uppercase text-[#C6963C] mb-3">
              What You Get
            </p>
            <h2 className="font-display font-semibold text-4xl md:text-5xl text-[#170F26] mb-4">
              Six things, all free
            </h2>
            <p className="text-[#6B6478] text-lg">
              Each one is paid for by someone on the other side of the platform
              — never by you.
            </p>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <Reveal key={f.title} delay={(i % 3) * 100}>
                  <div className="h-full bg-[#F4F1FB] rounded-2xl p-6 border border-[#170F26]/6 transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-xl hover:border-[#5B2E91]/25">
                    <span className="w-12 h-12 rounded-2xl bg-[#5B2E91]/10 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-[#5B2E91]" />
                    </span>
                    <h3 className="font-display font-semibold text-lg text-[#170F26] mb-2">
                      {f.title}
                    </h3>
                    <p className="text-[#4A4557] text-sm leading-relaxed mb-4">
                      {f.desc}
                    </p>
                    <p className="font-mono-label text-[10px] uppercase text-[#A99BC4] pt-3 border-t border-[#170F26]/8">
                      {f.funds}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* AFFORDABILITY CALCULATOR */}
      <section
        id="afford"
        className="relative px-6 md:px-16 py-24 bg-[#170F26] overflow-hidden"
      >
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#F4F1FB 1px, transparent 1px), linear-gradient(90deg, #F4F1FB 1px, transparent 1px)",
            backgroundSize: "34px 34px",
          }}
        />
        <div className="relative z-10 max-w-4xl mx-auto grid md:grid-cols-[1fr_1fr] gap-12 items-center">
          <Reveal>
            <p className="font-mono-label text-xs uppercase text-[#C6963C] mb-3">
              Know Before You Look
            </p>
            <h2 className="font-display font-semibold text-4xl md:text-5xl text-[#F4F1FB] mb-5 leading-tight">
              A rough sense of what you could borrow
            </h2>
            <p className="text-[#C9C4D9] text-lg leading-relaxed">
              This is a simple estimate, not a loan offer or financial advice —
              the real pre-qualification tool, connected to a bank or broker,
              arrives with launch.
            </p>
          </Reveal>

          <Reveal
            delay={120}
            className="bg-[#F4F1FB] rounded-2xl p-7 shadow-2xl"
          >
            <div className="flex items-center gap-2 mb-6">
              <Calculator className="w-4 h-4 text-[#5B2E91]" />
              <span className="font-mono-label text-[10px] uppercase text-[#6B6478]">
                Estimate only
              </span>
            </div>

            <label className="block mb-5">
              <span className="flex justify-between font-mono-label text-xs uppercase text-[#6B6478] mb-2">
                <span>Household income</span>
                <span className="text-[#5B2E91]">
                  ${income.toLocaleString()}
                </span>
              </span>
              <input
                type="range"
                min="40000"
                max="250000"
                step="5000"
                value={income}
                onChange={(e) => setIncome(Number(e.target.value))}
                className="w-full"
              />
            </label>

            <label className="block mb-6">
              <span className="flex justify-between font-mono-label text-xs uppercase text-[#6B6478] mb-2">
                <span>Deposit saved</span>
                <span className="text-[#5B2E91]">
                  ${deposit.toLocaleString()}
                </span>
              </span>
              <input
                type="range"
                min="0"
                max="400000"
                step="5000"
                value={deposit}
                onChange={(e) => setDeposit(Number(e.target.value))}
                className="w-full"
              />
            </label>

            <div className="rounded-2xl bg-[#5B2E91] p-5 text-center">
              <p className="font-mono-label text-[10px] uppercase text-[#B18ADB] mb-1">
                Rough borrowing range
              </p>
              <p className="font-display font-semibold text-3xl text-[#F4F1FB]">
                ~${borrowingEstimate.toLocaleString()}
              </p>
            </div>
            <p className="text-[#6B6478] text-xs mt-3 leading-relaxed">
              A simple rule-of-thumb estimate only — not financial advice. A
              bank or mortgage adviser will give you an accurate figure.
            </p>
          </Reveal>
        </div>
      </section>

      {/* WHY LIVJN */}
      <section
        id="why"
        className="px-6 md:px-16 py-24 bg-[#F4F1FB] text-center"
      >
        <Reveal className="max-w-2xl mx-auto mb-14">
          <p className="font-mono-label text-xs uppercase text-[#C6963C] mb-3">
            Why Livjn
          </p>
          <h2 className="font-display font-semibold text-4xl md:text-5xl text-[#170F26]">
            Built for the person searching, first
          </h2>
        </Reveal>
        <div className="grid sm:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {PILLARS.map((pillar, i) => {
            const Icon = pillar.icon;
            return (
              <Reveal key={pillar.title} delay={i * 120}>
                <div className="h-full bg-white rounded-2xl p-7 border border-[#170F26]/8 shadow-sm text-left transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-xl hover:border-[#5B2E91]/25">
                  {pillar.photo ? (
                    <img
                      src={pillar.photo}
                      alt=""
                      loading="lazy"
                      className="w-11 h-11 rounded-full object-cover mb-4"
                    />
                  ) : (
                    <span className="w-11 h-11 rounded-full bg-[#5B2E91]/10 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-[#5B2E91]" />
                    </span>
                  )}
                  <h3 className="font-display font-semibold text-lg text-[#170F26] mb-2">
                    {pillar.title}
                  </h3>
                  <p className="text-[#6B6478] text-sm leading-relaxed">
                    {pillar.desc}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* EARLY ACCESS CTA */}
      <section className="relative px-6 md:px-16 py-28 text-center overflow-hidden">
        <img
          src={PHOTOS.aerial}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#5B2E91]/88" />
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#F4F1FB 1px, transparent 1px), linear-gradient(90deg, #F4F1FB 1px, transparent 1px)",
            backgroundSize: "34px 34px",
          }}
        />
        <Reveal className="relative z-10 max-w-2xl mx-auto">
          <p className="font-mono-label text-xs uppercase text-[#F2CD82] mb-3">
            Get Early Access
          </p>
          <h2 className="font-display font-semibold text-4xl md:text-5xl text-[#F4F1FB] mb-5">
            Be first through the door
          </h2>
          <p className="text-[#E7E1F5] text-lg mb-8">
            Leave your email and we'll let you know the moment Livjn opens for
            real search — no spam, just one message when it's ready.
          </p>
          <button
            onClick={openModal}
            className="px-8 py-3.5 rounded-2xl bg-[#F4F1FB] hover:bg-white text-[#5B2E91] font-semibold shadow-lg hover:shadow-2xl hover:-translate-y-0.5 transition-all inline-flex items-center gap-2"
          >
            Join Early Access <ArrowRight className="w-4 h-4" />
          </button>
        </Reveal>
      </section>

      {/* FOOTER */}
      <footer className="px-6 md:px-16 py-10 bg-[#120B1E] text-[#A99BC4] text-xs">
        <div className="flex flex-col md:flex-row justify-between items-center max-w-6xl mx-auto gap-y-4">
          <div className="flex items-center gap-2.5">
            <img
              src="/images/logolivjn.png"
              alt="Livjn Logo"
              className="h-8 w-auto"
            />
          </div>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-[#B18ADB]" /> REAA 2008
              Compliant
            </span>
          </div>
          <p>© {new Date().getFullYear()} Livjn. All rights reserved.</p>
        </div>

        <div className="max-w-6xl mx-auto mt-6 pt-6 border-t border-[#F4F1FB]/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <button
            onClick={switchToPartner}
            className="font-mono-label text-[10px] uppercase text-[#6B6478] hover:text-[#B18ADB] flex items-center gap-1.5 transition-colors"
          >
            <ArrowLeftRight className="w-3.5 h-3.5" /> Real estate professional?
            Switch to the partner page
          </button>
        </div>
      </footer>

      {/* EARLY ACCESS MODAL */}
      {showModal && (
        <div
          className="enter-fade fixed inset-0 z-[100] bg-[#0C0714]/70 backdrop-blur-sm flex items-center justify-center px-6"
          onClick={() => setShowModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="modal-pop top-0 relative bg-[#F4F1FB] rounded-2xl shadow-2xl max-w-sm w-full px-8 py-9 text-center border-t-4 border-[#5B2E91]"
          >
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-[#6B6478] hover:text-[#170F26] hover:rotate-90 transition-all duration-300"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="w-14 h-14 rounded-full bg-[#5B2E91]/10 flex items-center justify-center mx-auto mb-5">
              <KeyRound className="w-6 h-6 text-[#5B2E91] pulse-dot" />
            </div>
            <h3 className="font-display font-semibold text-2xl text-[#170F26] mb-3">
              Nothing's live yet
            </h3>
            <p className="text-[#4A4557] leading-relaxed">
              Livjn hasn't launched, so there's no account to create — just
              leave your email and we'll reach out the moment real search opens.
            </p>
            <a
              href="mailto:hello@livjn.co.nz?subject=Early%20access%20request"
              className="mt-7 inline-block px-7 py-2.5 rounded-2xl bg-[#5B2E91] hover:bg-[#7A46B8] text-[#F4F1FB] font-semibold hover:-translate-y-0.5 hover:shadow-lg transition-all"
            >
              Email Us To Join
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================= PARTNER PAGE ============================= */

function PartnerPage({ switchToUser, setView, view }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");
  const [showModal, setShowModal] = useState(false);
  const [activeServiceId, setActiveServiceId] = useState(1);

  const navItems = [
    { label: "The Engine", href: "#hero" },
    { label: "Revenue Streams", href: "#streams" },
    { label: "Subscriptions", href: "#tiers" },
    { label: "Why It Works", href: "#why" },
    { label: "Partner With Us", href: "#partner" },
  ];

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const ids = navItems.map((item) => item.href.replace("#", ""));
    const sections = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 },
    );
    sections.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const el = document.querySelector(id);
    if (!el) return;
    setTimeout(
      () => el.scrollIntoView({ behavior: "smooth", block: "start" }),
      150,
    );
  };

  const openModal = () => setShowModal(true);

  const SERVICES = [
    {
      id: 1,
      num: "01",
      icon: KeyRound,
      title: "Agent Subscriptions",
      tagline: "The recurring core",
      audience: "Agents",
      impact: 5,
      angle: -90,
      stat: "Planned pricing: NZ$100–500+ / month, four tiers",
      desc: "Agents pay monthly or annual fees to list properties, manage bigger portfolios, and unlock analytics and marketing tools. Four tiers — Starter, Pro, Superstar, Corporate — scale with the size of their book.",
    },
    {
      id: 2,
      num: "02",
      icon: Rocket,
      title: "Featured Listings & Promotion",
      tagline: "Visibility, sold separately",
      audience: "Agents",
      impact: 5,
      angle: -45,
      stat: "Priced like Google Ads, sized like a boosted post",
      desc: "Not every listing gets equal exposure. Agents pay to rank at the top of search, highlight a property, or become a featured agent in a suburb — the more reach they want, the more it costs.",
      photo: PHOTOS.featuredHome,
    },
    {
      id: 3,
      num: "03",
      icon: Building2,
      title: "Developer Advertising",
      tagline: "Big launches, big budgets",
      audience: "Developers",
      impact: 4,
      angle: 0,
      stat: "Homepage banners · sponsored projects · lead-gen",
      desc: "Developers launching new apartment or housing projects buy homepage placements, branded landing pages, and content campaigns. Fees scale with campaign length and prominence, some billed per lead.",
      photo: PHOTOS.newBuild,
    },
    {
      id: 4,
      num: "04",
      icon: Zap,
      title: "Lead Sales",
      tagline: "Every enquiry has a price",
      audience: "Agents",
      impact: 4,
      angle: 45,
      stat: "Priced by intent — a lead can start a six-figure sale",
      desc: "A 'Contact Agent' or 'Request Information' click is a qualified buyer lead. Depending on the market, a single lead can be worth anywhere from tens to hundreds of dollars to the agent who receives it.",
    },
    {
      id: 5,
      num: "05",
      icon: Landmark,
      title: "Mortgage Referrals",
      tagline: "Financing, referred",
      audience: "Lenders",
      impact: 3,
      angle: 90,
      stat: "Commission on settlement, not on enquiry",
      desc: "Almost every buyer also needs financing. We refer them to banks and mortgage brokers, and collect a referral commission once the loan they arrange actually settles.",
    },
    {
      id: 6,
      num: "06",
      icon: LayoutDashboard,
      title: "Developer SaaS",
      tagline: "Software for the build",
      audience: "Developers",
      impact: 3,
      angle: 135,
      stat: "Licensing + implementation, billed annually",
      desc: "Developers run their project sales on our software — tracking units, generating contracts, accepting bookings, and monitoring performance — under an implementation and ongoing licensing fee.",
    },
    {
      id: 7,
      num: "07",
      icon: BarChart3,
      title: "Data & Analytics",
      tagline: "The market, quantified",
      audience: "Industry",
      impact: 3,
      angle: 180,
      stat: "Demand, pricing, and suburb trends, packaged",
      desc: "Every search and enquiry becomes market signal — buyer demand, price movement, days on market, hot suburbs. Banks, valuers, developers and investors subscribe to see it first.",
    },
    {
      id: 8,
      num: "08",
      icon: Award,
      title: "Events & Awards",
      tagline: "Where the industry meets",
      audience: "Industry",
      impact: 2,
      angle: 225,
      stat: "Sponsorship, booths, tickets — and relationships",
      desc: "Property expos, developer showcases, and industry awards bring in sponsorship and ticket revenue, while keeping us close to the developers we depend on for streams 03 and 06.",
    },
  ];

  const TIERS = [
    {
      name: "Starter",
      blurb: "For agents building their first book",
      features: [
        "Up to 10 active listings",
        "Standard search placement",
        "Basic enquiry inbox",
      ],
    },
    {
      name: "Pro",
      blurb: "The most common agent plan",
      features: [
        "Up to 40 active listings",
        "Priority search placement",
        "Lead analytics dashboard",
        "2 boosted listings / month",
      ],
      featured: true,
    },
    {
      name: "Superstar",
      blurb: "For top billers who want top spot",
      features: [
        "Unlimited listings",
        "Top-of-search placement",
        "Personal brand page",
        "8 boosted listings / month",
      ],
    },
    {
      name: "Corporate",
      blurb: "For offices and franchise groups",
      features: [
        "Multi-agent office account",
        "Team performance analytics",
        "Custom advertising packages",
        "Dedicated account manager",
      ],
    },
  ];

  const STATS = [
    {
      value: "Pre-Launch",
      label: "Livjn is being built now — this is the model, not a track record",
    },
    {
      value: "8",
      label: "Revenue lines designed before the first listing goes live",
    },
    { value: "4", label: "Agent subscription tiers, priced from day one" },
    {
      value: "1",
      label: "Platform every stream runs through — nothing bolted on later",
    },
  ];

  const PILLARS = [
    {
      icon: Users,
      title: "Recurring, not one-off",
      desc: "Agent subscriptions renew monthly or annually, so the base of the business doesn't reset to zero every quarter.",
    },
    {
      icon: Zap,
      title: "Demand-priced upside",
      desc: "Boosts, featured slots and leads are sold at the moment they're worth the most — pricing that moves with market heat.",
    },
    {
      icon: BarChart3,
      title: "A data moat, not just a listing site",
      desc: "Every search and enquiry adds to a dataset that gets more valuable to banks, developers and investors the longer it runs.",
    },
  ];

  const activeService =
    SERVICES.find((s) => s.id === activeServiceId) ?? SERVICES[0];

  const CX = 260;
  const CY = 250;
  const R = 178;
  const nodeR = { 5: 32, 4: 28, 3: 24, 2: 20 };
  const nodes = SERVICES.map((s) => {
    const rad = (s.angle * Math.PI) / 180;
    return {
      ...s,
      x: CX + R * Math.cos(rad),
      y: CY + R * Math.sin(rad),
      r: nodeR[s.impact],
    };
  });

  return (
    <div className=" min-h-screen bg-[#F4F1FB] font-body text-[#170F26] overflow-x-hidden scroll-smooth">
      <style>{FONT_IMPORT_STYLE}</style>

      {/* NAVBAR */}
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          navScrolled
            ? "bg-[#4A2478] shadow-[0_8px_30px_rgba(23,15,38,0.45)] border-b border-white/10"
            : "bg-[#5B2E91]/95 backdrop-blur-md border-b border-white/5"
        }`}
      >
        <div
          className={`max-w-7xl mx-auto flex justify-between items-center px-6 transition-all duration-300 ${navScrolled ? "py-2.5" : "py-3.5"}`}
        >
          <button
            onClick={() => scrollToSection("#hero")}
            className="flex items-center gap-2.5"
          >
            <img src="/images/logolivjn_w.png" className="h-8" />
          </button>

          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => {
              const id = item.href.replace("#", "");
              const isActive = activeSection === id;
              return (
                <button
                  key={item.label}
                  onClick={() => scrollToSection(item.href)}
                  className={`relative px-3 py-2 text-[13px] font-medium uppercase tracking-wide whitespace-nowrap transition-colors ${
                    isActive
                      ? "text-[#F2CD82]"
                      : "text-[#E4D9F5]/80 hover:text-white"
                  }`}
                >
                  {item.label}
                  <span
                    className={`absolute left-3 right-3 -bottom-[1px] h-[2px] bg-[#F2CD82] rounded-full origin-center transition-transform duration-300 ease-out ${
                      isActive ? "scale-x-100" : "scale-x-0"
                    }`}
                  />
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-3">
            <ViewSwitch view={view} setView={setView} dark />
            <button
              onClick={openModal}
              className="hidden md:flex px-6 py-2.5 rounded-2xl bg-[#170F26] hover:bg-[#241A38] text-[#F4F1FB] text-sm font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all whitespace-nowrap items-center gap-1.5"
            >
              Talk To Partnerships
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <button
              className="lg:hidden text-[#F4F1FB] p-2 -mr-2 rounded-2xl hover:bg-white/10 transition-colors"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <div className="flex items-center gap-x-3">
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      switchToUser();
                    }}
                    className="font-mono-label text-[11px] uppercase text-[#F2CD82] flex items-center gap-1.5"
                  >
                    <ArrowLeftRight className="w-3.5 h-3.5" /> For Buyer
                  </button>{" "}
                  <Menu className="w-6 h-6" />
                </div>
              )}
            </button>
          </div>
        </div>

        {menuOpen && (
          <>
            <div
              className="enter-fade fixed inset-0 bg-[#170F26]/90 backdrop-blur-sm z-40 h-screen lg:hidden mt-16"
              onClick={() => setMenuOpen(false)}
            />
            <div className="enter-up fixed top-16 left-0 right-0 z-50 lg:hidden bg-[#5B2E91] border-t border-white/15 flex flex-col items-center py-6 space-y-4">
              {navItems.map((item, i) => (
                <button
                  key={item.label}
                  onClick={() => {
                    setMenuOpen(false);
                    scrollToSection(item.href);
                  }}
                  className="enter-up text-lg text-[#F4F1FB] tracking-wide"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  {item.label}
                </button>
              ))}
              <span className="w-10 h-px bg-white/20 my-1" />

              <button
                onClick={() => {
                  setMenuOpen(false);
                  openModal();
                }}
                className="px-6 py-2.5 rounded-2xl bg-[#170F26] text-[#F4F1FB] text-sm font-semibold flex items-center gap-1.5"
              >
                Talk To Partnerships <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </>
        )}
      </nav>

      {/* HERO — the Revenue Engine */}
      <section
        id="hero"
        className="relative min-h-screen flex items-center px-6 md:px-16 pt-32 pb-20 bg-[#170F26] overflow-hidden"
      >
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#F4F1FB 1px, transparent 1px), linear-gradient(90deg, #F4F1FB 1px, transparent 1px)",
            backgroundSize: "34px 34px",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto w-full grid md:grid-cols-[1fr_1fr] gap-16 items-center">
          <div>
            <span className="enter-up inline-flex items-center gap-1.5 font-mono-label text-[10px] uppercase text-[#C6963C] border border-[#C6963C]/40 rounded-full px-3 py-1 mb-5">
              <Sparkles className="w-3 h-3 pulse-dot" /> Just Started · Eight
              Revenue Lines Mapped
            </span>

            <h1
              className="enter-up font-display font-semibold text-5xl md:text-6xl leading-[1.08] text-[#F4F1FB]"
              style={{ animationDelay: "80ms" }}
            >
              Real estate's
              <br />
              <span className="text-[#B18ADB]">revenue engine.</span>
            </h1>

            <p
              className="enter-up mt-6 max-w-lg text-[#C9C4D9] text-lg leading-relaxed"
              style={{ animationDelay: "160ms" }}
            >
              Livjn is just getting off the ground — there are no listings or
              agents on the platform yet. What we do have is the model:
              subscriptions, promotion, leads, financing, software and data, all
              designed to run through one platform from the very first sign-up.
            </p>

            <ul
              className="enter-up mt-7 space-y-3"
              style={{ animationDelay: "240ms" }}
            >
              {[
                "Recurring agent subscriptions, built in from day one",
                "Demand-priced promotion, leads, and developer campaigns",
                "A data and software layer banks and developers can grow into",
              ].map((point) => (
                <li key={point} className="flex items-start gap-3">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-[#5B2E91] flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3 text-[#F4F1FB]" />
                  </span>
                  <span className="text-[#EDEAF6]">{point}</span>
                </li>
              ))}
            </ul>

            <div
              className="enter-up mt-9 flex flex-wrap items-center gap-4"
              style={{ animationDelay: "320ms" }}
            >
              <button
                onClick={openModal}
                className="px-7 py-3 rounded-2xl bg-[#5B2E91] hover:bg-[#7A46B8] text-[#F4F1FB] font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all flex items-center gap-2"
              >
                Talk To Partnerships <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => scrollToSection("#streams")}
                className="px-7 py-3 rounded-2xl border border-[#F4F1FB]/25 text-[#F4F1FB] font-semibold hover:bg-[#F4F1FB]/10 hover:-translate-y-0.5 transition-all"
              >
                See The Eight Streams
              </button>
            </div>
          </div>

          <div
            className="relative enter-up"
            style={{ animationDelay: "200ms" }}
          >
            <div className="bg-[#F4F1FB] rounded-2xl shadow-2xl p-6 pb-4 transition-shadow duration-500 hover:shadow-[0_25px_60px_-15px_rgba(91,46,145,0.55)]">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono-label text-[10px] uppercase text-[#6B6478]">
                  The Engine · Click a node
                </span>
                <CircuitBoard className="w-4 h-4 text-[#5B2E91]" />
              </div>

              <svg viewBox="0 0 520 500" className="w-full">
                {nodes.map((node) => (
                  <line
                    key={`spoke-${node.id}`}
                    x1={CX}
                    y1={CY}
                    x2={node.x}
                    y2={node.y}
                    stroke={node.id === activeServiceId ? "#C6963C" : "#170F26"}
                    strokeOpacity={node.id === activeServiceId ? 1 : 0.12}
                    strokeWidth={node.id === activeServiceId ? 2 : 1}
                    className="transition-all duration-500 ease-out"
                  />
                ))}

                <circle cx={CX} cy={CY} r="46" fill="#5B2E91" />
                <circle
                  cx={CX}
                  cy={CY}
                  r="46"
                  fill="none"
                  stroke="#B18ADB"
                  strokeOpacity="0.5"
                  strokeWidth="1.5"
                  className="pulse-dot"
                  style={{ transformOrigin: `${CX}px ${CY}px` }}
                />
                <text
                  x={CX}
                  y={CY + 5}
                  textAnchor="middle"
                  className="font-display"
                  fontSize="15"
                  fontWeight="600"
                  fill="#F4F1FB"
                >
                  LIVJN
                </text>

                {nodes.map((node) => {
                  const isActive = node.id === activeServiceId;
                  return (
                    <g
                      key={node.id}
                      onClick={() => setActiveServiceId(node.id)}
                      className="cursor-pointer transition-transform duration-300 ease-out hover:scale-110"
                      style={{ transformOrigin: `${node.x}px ${node.y}px` }}
                    >
                      <circle
                        cx={node.x}
                        cy={node.y}
                        r={node.r}
                        fill={isActive ? "#5B2E91" : "#FFFFFF"}
                        stroke={isActive ? "#C6963C" : "#170F26"}
                        strokeOpacity={isActive ? 1 : 0.18}
                        strokeWidth={isActive ? 2.5 : 1}
                        className="transition-all duration-300 ease-out"
                      />
                      <text
                        x={node.x}
                        y={node.y + 4}
                        textAnchor="middle"
                        className="font-mono-label transition-all duration-300 ease-out"
                        fontSize="11"
                        fontWeight="700"
                        fill={isActive ? "#F4F1FB" : "#170F26"}
                      >
                        {node.num}
                      </text>
                    </g>
                  );
                })}
              </svg>

              <p className="font-mono-label text-[10px] uppercase text-[#6B6478] text-center -mt-1">
                Node size = revenue weight
              </p>
            </div>

            <div
              key={activeService.id}
              className="fade-panel mt-4 rounded-2xl bg-[#5B2E91] p-5 shadow-xl"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-2xl bg-[#F4F1FB]/10 flex items-center justify-center shrink-0">
                    <activeService.icon className="w-5 h-5 text-[#B18ADB]" />
                  </span>
                  <div>
                    <p className="font-mono-label text-[10px] uppercase text-[#B18ADB]">
                      {activeService.num} · {activeService.audience}
                    </p>
                    <h3 className="font-display font-semibold text-lg text-[#F4F1FB] leading-tight">
                      {activeService.title}
                    </h3>
                  </div>
                </div>
                <ImpactMeter level={activeService.impact} onDark />
              </div>
              <p className="mt-3 text-[#EDEAF6] text-sm leading-relaxed">
                {activeService.desc}
              </p>
              <p className="mt-3 font-mono-label text-[11px] text-[#C6963C]">
                {activeService.stat}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="px-6 md:px-16 py-14 bg-[#F4F1FB] border-b border-[#170F26]/8">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {STATS.map((stat, i) => (
            <Reveal key={stat.label} delay={i * 90}>
              <p className="font-mono-label text-3xl md:text-4xl text-[#5B2E91]">
                {stat.value}
              </p>
              <p className="text-[#6B6478] text-sm mt-2 leading-snug">
                {stat.label}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* REVENUE STREAMS */}
      <section id="streams">
        <div className="px-6 md:px-16 pt-24 pb-14 bg-[#F4F1FB]">
          <div className="max-w-3xl mx-auto text-center">
            <p className="font-mono-label text-xs uppercase text-[#C6963C] mb-3">
              Revenue Streams
            </p>
            <h2 className="font-display font-semibold text-4xl md:text-5xl text-[#170F26] mb-4">
              Eight ways the platform earns
            </h2>
            <p className="text-[#6B6478] text-lg mb-10">
              Ranked by weight, not by launch order — subscriptions and
              promotion carry the business, everything else compounds on top.
              Jump to any stream, or scroll through all eight below.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {SERVICES.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => scrollToSection(`#stream-${s.id}`)}
                  className="enter-up font-mono-label text-[11px] uppercase px-4 py-2 rounded-full border border-[#170F26]/15 text-[#6B6478] hover:border-[#5B2E91] hover:text-[#5B2E91] hover:-translate-y-0.5 transition-all"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  {s.num} {s.title}
                </button>
              ))}
            </div>
          </div>
        </div>

        {SERVICES.map((service, index) => (
          <FeatureSection key={service.id} service={service} index={index} />
        ))}
      </section>

      {/* SUBSCRIPTION TIERS */}
      <section
        id="tiers"
        className="relative px-6 md:px-16 py-24 bg-[#170F26] overflow-hidden"
      >
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#F4F1FB 1px, transparent 1px), linear-gradient(90deg, #F4F1FB 1px, transparent 1px)",
            backgroundSize: "34px 34px",
          }}
        />
        <div className="relative z-10 max-w-6xl mx-auto">
          <p className="font-mono-label text-xs uppercase text-[#C6963C] mb-3 text-center">
            Revenue Stream 01, In Detail
          </p>
          <h2 className="font-display font-semibold text-4xl md:text-5xl text-[#F4F1FB] mb-4 text-center">
            Four agent subscription tiers
          </h2>
          <p className="text-[#C9C4D9] text-lg max-w-2xl mx-auto mb-14 text-center">
            New agents will start on Starter or Pro. The upgrade path from there
            is where most of the recurring revenue is designed to grow.
          </p>

          <div className="grid md:grid-cols-4 gap-6">
            {TIERS.map((tier, i) => (
              <Reveal key={tier.name} delay={i * 100}>
                <div
                  className={`relative rounded-2xl p-7 flex flex-col h-full transition-all duration-500 ease-out hover:-translate-y-1.5 ${
                    tier.featured
                      ? "bg-[#F4F1FB] border-2 border-[#C6963C] shadow-2xl hover:shadow-[0_25px_50px_-15px_rgba(198,150,60,0.45)]"
                      : "bg-[#241A38] border border-[#F4F1FB]/10 hover:border-[#7A46B8]/60 hover:shadow-[0_20px_45px_-15px_rgba(122,70,184,0.45)]"
                  }`}
                >
                  {tier.featured && (
                    <span className="absolute -top-3 right-6 bg-[#C6963C] text-[#170F26] font-mono-label text-[10px] uppercase px-3 py-1 rounded-full">
                      Most Common
                    </span>
                  )}
                  <h3
                    className={`font-display font-semibold text-2xl mb-1 ${tier.featured ? "text-[#170F26]" : "text-[#F4F1FB]"}`}
                  >
                    {tier.name}
                  </h3>
                  <p
                    className={`text-sm mb-6 ${tier.featured ? "text-[#6B6478]" : "text-[#A99BC4]"}`}
                  >
                    {tier.blurb}
                  </p>
                  <ul className="space-y-3 flex-1">
                    {tier.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5">
                        <Check
                          className={`w-4 h-4 mt-0.5 shrink-0 ${tier.featured ? "text-[#C6963C]" : "text-[#B18ADB]"}`}
                        />
                        <span
                          className={`text-sm ${tier.featured ? "text-[#4A4557]" : "text-[#D9D3EC]"}`}
                        >
                          {f}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY IT WORKS */}
      <section id="why" className="px-6 md:px-16 py-24 bg-[#F4F1FB]">
        <div className="max-w-6xl mx-auto grid md:grid-cols-[0.9fr_1.1fr] gap-16 items-start">
          <Reveal>
            <p className="font-mono-label text-xs uppercase text-[#C6963C] mb-3">
              Why It Works
            </p>
            <h2 className="font-display font-semibold text-4xl md:text-5xl text-[#170F26] mb-6 leading-tight">
              Three revenue lines feed the other five
            </h2>
            <p className="text-[#4A4557] text-lg leading-relaxed">
              Agent subscriptions and promotion are built to fund the traffic.
              That traffic is designed to produce leads and data. The data and
              the developer relationships it builds are meant to fund the SaaS,
              mortgage referral, and events lines. Nothing here is a separate
              business — it's one funnel with eight exits.
            </p>
            <p className="text-[#6B6478] text-base leading-relaxed mt-4">
              It's the same shape that took platforms like PropertyGuru from a
              single listings site to a multi-line property business across
              Southeast Asia. Livjn is building that model for Canterbury from
              day one, rather than bolting it on after the fact.
            </p>
            <div className="mt-6 rounded-2xl overflow-hidden shadow-lg">
              <img
                src={PHOTOS.agentMeeting}
                alt="Agent meeting with prospective clients"
                loading="lazy"
                className="w-full h-48 object-cover"
              />
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-1 gap-5">
            {PILLARS.map((pillar, i) => {
              const Icon = pillar.icon;
              return (
                <Reveal key={pillar.title} delay={i * 120}>
                  <div className="flex items-start gap-4 bg-white rounded-2xl p-6 border border-[#170F26]/8 shadow-sm transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-xl hover:border-[#5B2E91]/30">
                    <span className="w-11 h-11 rounded-full bg-[#5B2E91]/10 flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-[#5B2E91]" />
                    </span>
                    <div>
                      <h3 className="font-display font-semibold text-lg text-[#170F26]">
                        {pillar.title}
                      </h3>
                      <p className="text-[#6B6478] text-sm mt-1 leading-relaxed">
                        {pillar.desc}
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
            <Reveal delay={360} className="grid grid-cols-3 gap-3">
              {[PHOTOS.agent1, PHOTOS.agent2, PHOTOS.agent3].map((src) => (
                <img
                  key={src}
                  src={src}
                  alt="Founding team member"
                  loading="lazy"
                  className="w-full aspect-square object-cover rounded-2xl shadow-sm transition-transform duration-500 hover:scale-105"
                />
              ))}
            </Reveal>
          </div>
        </div>
      </section>

      {/* PARTNER CTA */}
      <section
        id="partner"
        className="relative px-6 md:px-16 py-24 text-center overflow-hidden"
      >
        <img
          src={PHOTOS.aerial}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#5B2E91]/88" />
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#F4F1FB 1px, transparent 1px), linear-gradient(90deg, #F4F1FB 1px, transparent 1px)",
            backgroundSize: "34px 34px",
          }}
        />
        <Reveal className="relative z-10 max-w-2xl mx-auto">
          <p className="font-mono-label text-xs uppercase text-[#F2CD82] mb-3">
            Partner With Livjn
          </p>
          <h2 className="font-display font-semibold text-4xl md:text-5xl text-[#F4F1FB] mb-5">
            Help shape the first cohort
          </h2>
          <p className="text-[#E7E1F5] text-lg mb-8">
            We're onboarding our first agents, developers and lenders before
            public launch. Tell us which of the eight streams brought you here
            and we'll follow up directly — there's no call centre yet, just us.
          </p>
          <button
            onClick={openModal}
            className="px-8 py-3.5 rounded-2xl bg-[#F4F1FB] hover:bg-white text-[#5B2E91] font-semibold shadow-lg hover:shadow-2xl hover:-translate-y-0.5 transition-all inline-flex items-center gap-2"
          >
            Talk To Us <ArrowRight className="w-4 h-4" />
          </button>
        </Reveal>
      </section>

      {/* FOOTER */}
      <footer className="px-6 md:px-16 py-10 bg-[#120B1E] text-[#A99BC4] text-xs">
        <div className="flex flex-col md:flex-row justify-between items-center max-w-6xl mx-auto gap-y-4">
          <div className="flex items-center gap-2.5">
            <img
              src="/images/logolivjn_yellow.png"
              alt="Livjn Logo"
              className="h-8 w-auto"
            />
          </div>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-[#B18ADB]" /> REAA 2008
              Compliant
            </span>
          </div>
          <p>© {new Date().getFullYear()} Livjn. All rights reserved.</p>
        </div>

        <div className="max-w-6xl mx-auto mt-6 pt-6 border-t border-[#F4F1FB]/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <button
            onClick={switchToUser}
            className="font-mono-label text-[10px] uppercase text-[#6B6478] hover:text-[#B18ADB] flex items-center gap-1.5 transition-colors"
          >
            <ArrowLeftRight className="w-3.5 h-3.5" /> Looking for a home?
            Switch to the buyer page
          </button>
        </div>
      </footer>

      {/* PARTNER MODAL */}
      {showModal && (
        <div
          className="enter-fade fixed inset-0 z-[100] bg-[#0C0714]/70 backdrop-blur-sm flex items-center justify-center px-6"
          onClick={() => setShowModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="modal-pop relative bg-[#F4F1FB] rounded-2xl shadow-2xl max-w-sm w-full px-8 py-9 text-center border-t-4 border-[#5B2E91]"
          >
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-[#6B6478] hover:text-[#170F26] hover:rotate-90 transition-all duration-300"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="w-14 h-14 rounded-full bg-[#5B2E91]/10 flex items-center justify-center mx-auto mb-5">
              <CircuitBoard className="w-6 h-6 text-[#5B2E91] pulse-dot" />
            </div>
            <h3 className="font-display font-semibold text-2xl text-[#170F26] mb-3">
              We're building this now
            </h3>
            <p className="text-[#4A4557] leading-relaxed">
              Livjn is pre-launch, so there's no call queue — just a small team.
              Tell us whether you're an agent, a developer, a lender, or after
              the data, and we'll reply ourselves, directly.
            </p>
            <a
              href="mailto:partners@livjn.co.nz?subject=Founding%20partner%20enquiry"
              className="mt-7 inline-block px-7 py-2.5 rounded-2xl bg-[#5B2E91] hover:bg-[#7A46B8] text-[#F4F1FB] font-semibold hover:-translate-y-0.5 hover:shadow-lg transition-all"
            >
              Email The Team
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

function ImpactMeter({ level, onDark }) {
  const bars = [1, 2, 3, 4, 5];
  return (
    <div
      className="flex items-end gap-[3px] shrink-0"
      title={`Revenue impact ${level}/5`}
    >
      {bars.map((bar) => (
        <span
          key={bar}
          className="w-1.5 rounded-[1px]"
          style={{
            height: `${8 + bar * 3}px`,
            backgroundColor:
              bar <= level
                ? "#C6963C"
                : onDark
                  ? "rgba(244,241,251,0.15)"
                  : "rgba(23,15,38,0.12)",
          }}
        />
      ))}
    </div>
  );
}

function FeatureSection({ service, index }) {
  const Icon = service.icon;
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const reversed = index % 2 === 1;
  const onDark = index % 3 === 2;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const bg = onDark
    ? "bg-[#170F26]"
    : index % 2 === 0
      ? "bg-white"
      : "bg-[#F4F1FB]";
  const titleColor = onDark ? "text-[#F4F1FB]" : "text-[#170F26]";
  const bodyColor = onDark ? "text-[#C9C4D9]" : "text-[#4A4557]";
  const taglineColor = onDark ? "text-[#B18ADB]" : "text-[#6B6478]";
  const numStroke = onDark ? "rgba(244,241,251,0.14)" : "rgba(23,15,38,0.09)";

  return (
    <div
      id={`stream-${service.id}`}
      ref={ref}
      className={`px-6 md:px-16 py-20 border-t ${onDark ? "border-white/10" : "border-[#170F26]/8"} ${bg} transition-all duration-700 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
    >
      <div
        className={`max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center ${
          reversed ? "md:[&>*:first-child]:order-2" : ""
        }`}
      >
        <div
          className={`relative rounded-2xl p-10 flex flex-col justify-between min-h-[280px] overflow-hidden transition-all duration-500 ease-out hover:-translate-y-1.5 ${
            onDark
              ? "bg-[#241A38] border border-white/10 hover:shadow-[0_20px_45px_-15px_rgba(122,70,184,0.5)]"
              : "bg-white border border-[#170F26]/8 shadow-sm hover:shadow-xl"
          }`}
        >
          {service.photo ? (
            <>
              <img
                src={service.photo}
                alt=""
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#170F26]/85 via-[#170F26]/40 to-[#170F26]/10" />
            </>
          ) : (
            <span
              aria-hidden="true"
              className="absolute -bottom-6 -right-4 font-display font-semibold select-none pointer-events-none"
              style={{
                fontSize: "9rem",
                lineHeight: 1,
                WebkitTextStroke: `1.5px ${numStroke}`,
                color: "transparent",
              }}
            >
              {service.num}
            </span>
          )}
          <div className="relative z-10 flex items-center justify-between">
            <span
              className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-transform duration-500 ease-out ${
                service.photo
                  ? "bg-[#5B2E91]"
                  : onDark
                    ? "bg-[#5B2E91]"
                    : "bg-[#5B2E91]/10"
              }`}
            >
              <Icon
                className={`w-6 h-6 ${service.photo || onDark ? "text-[#F4F1FB]" : "text-[#5B2E91]"}`}
              />
            </span>
            <span
              className={`font-mono-label text-[10px] uppercase px-3 py-1.5 rounded-full border ${
                service.photo
                  ? "border-white/40 text-white"
                  : onDark
                    ? "border-white/20 text-[#C9C4D9]"
                    : "border-[#170F26]/15 text-[#6B6478]"
              }`}
            >
              {service.audience}
            </span>
          </div>
          <div className="relative z-10">
            <p
              className={`font-mono-label text-xs uppercase mb-2 ${service.photo ? "text-[#F2CD82]" : taglineColor}`}
            >
              Revenue Impact
            </p>
            <ImpactMeter
              level={service.impact}
              onDark={onDark || !!service.photo}
            />
          </div>
        </div>

        <div
          className={`transition-all duration-700 ease-out delay-150 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <p
            className={`font-mono-label text-xs uppercase mb-3 ${onDark ? "text-[#C6963C]" : "text-[#C6963C]"}`}
          >
            Stream {service.num} of 08
          </p>
          <h3
            className={`font-display font-semibold text-3xl md:text-4xl mb-2 ${titleColor}`}
          >
            {service.title}
          </h3>
          <p className={`italic mb-5 ${taglineColor}`}>{service.tagline}</p>
          <p className={`text-lg leading-relaxed mb-6 ${bodyColor}`}>
            {service.desc}
          </p>
          <p
            className={`font-mono-label text-[12px] inline-flex items-center gap-1.5 pt-4 border-t ${
              onDark
                ? "border-white/10 text-[#B18ADB]"
                : "border-[#170F26]/8 text-[#5B2E91]"
            }`}
          >
            <ChevronRight className="w-3.5 h-3.5" /> {service.stat}
          </p>
        </div>
      </div>
    </div>
  );
}
